import { Backdrop, Box, Button, Divider, IconButton, Paper, Portal, Typography, Unstable_TrapFocus } from "@mui/material";
import { FC, createContext, useState, useRef, useEffect } from "react";
import { useCallback } from "react";
import { useContext } from "react";
import { PrismLight as SyntaxHighlighter } from 'react-syntax-highlighter';
import sql from 'react-syntax-highlighter/dist/esm/languages/prism/sql'
import dracula from 'react-syntax-highlighter/dist/cjs/styles/prism/dracula';
import MinimizeIcon from '@mui/icons-material/Minimize';
import ArrowsExpandIcon from '~/icons/ArrowsExpand';
import XIcon from '~/icons/X';
import WrapTextIcon from '@mui/icons-material/WrapText';


SyntaxHighlighter.registerLanguage('sql', sql)


export interface LogViewerControllerProps {
  logContent: string;
  isViewerOpen: boolean;
  showLogInViewer: (e:MouseEvent, logContent: string) => void;
  closeViewer: () => void;
}

const LogViewerContext = createContext<LogViewerControllerProps>({
  logContent: '',
  isViewerOpen: false,
  showLogInViewer: () => {},
  closeViewer: () => {},
})

export const LogViewerProvider = LogViewerContext.Provider

export const useLogViewerController = (): LogViewerControllerProps => {
  const [isViewerOpen, setIsViewerOpen] = useState(false)
  const [logContent, setLogContent] = useState('')
  const nodeToRestore = useRef<HTMLElement|null>(null)

  const showLogInViewer = useCallback((e:MouseEvent, content: string) => {
    nodeToRestore.current = e.currentTarget as HTMLElement
    setLogContent(content)
    setIsViewerOpen(true)
  }, [])
  
  const closeViewer = useCallback(() => {
    if (nodeToRestore.current) {
      nodeToRestore.current.focus()
      nodeToRestore.current = null
    }
    setIsViewerOpen(false)
    setLogContent('')
  }, [])
  
  useEffect(() => {
    return () => {
      nodeToRestore.current = null
    }
  }, [])

  return {
    logContent,
    isViewerOpen,
    showLogInViewer,
    closeViewer,
  }
}

export const useLogViewerContext = () => {
  const context = useContext<LogViewerControllerProps>(LogViewerContext)
  return context
}

const LogViewer: FC = () => {
  const language = 'sql'
  const { logContent, isViewerOpen, closeViewer } = useLogViewerContext()
  const [isFullScreen, setIsFullScreen] = useState<boolean>(true);
  const [wrapLine, setWrapLine] = useState(false)

  const handleExitFullScreen = (): void => {
    setIsFullScreen(false);
  };

  const handleEnterFullScreen = (): void => {
    setIsFullScreen(true);
  };

  const sentinalStartRef = useRef<HTMLElement | null>(null)
  useEffect(() => {
    setTimeout(() => {
      if (isViewerOpen && sentinalStartRef.current) {
        sentinalStartRef.current.focus()
      }
    }, 5)
  }, [isViewerOpen])

  if (!isViewerOpen) {
    return null
  }

  return (
    <Portal>
      <Backdrop open={isFullScreen} />
      <Paper
        sx={{
          bottom: 0,
          margin: 3,
          maxHeight: (theme) => `calc(100% - ${theme.spacing(6)})`,
          maxWidth: (theme) => `calc(100% - ${theme.spacing(6)})`,
          minHeight: 500,
          outline: 'none',
          position: 'fixed',
          right: 0,
          width: 600,
          zIndex: 2000,
          height: 500,
          ...(
            isFullScreen && {
              height: '100%',
              width: '100%'
            }
          )
        }}
        tabIndex={-1}
        elevation={12}
        role="dialog"
        aria-labelledby="error-log-viewer"
      >
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            px: 2,
            py: 1
          }}
        >
          <Typography
            variant="h6"
            color="textPrimary"
            id="error-log-viewer"
          >
            Error Log
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ width: 0, height: 0 }} tabIndex={0} ref={sentinalStartRef} />
          <IconButton 
            aria-label="toggle wrap line"
            onClick={() => setWrapLine(!wrapLine)}
            sx={{ mr: 2, color: wrapLine ? "text.secondary" : 'default' }}
          >
            <WrapTextIcon fontSize="small" />
          </IconButton>
          {
            isFullScreen
              ? (
                <IconButton aria-label="exit full screen" onClick={handleExitFullScreen}>
                  <MinimizeIcon fontSize="small" />
                </IconButton>
              )
              : (
                <IconButton aria-label="enter full screen" onClick={handleEnterFullScreen}>
                  <ArrowsExpandIcon fontSize="small" />
                </IconButton>
              )
          }
          <IconButton aria-label="close" onClick={closeViewer}>
            <XIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box
          sx={{
            display: 'flex',
            height: 'calc(100% - 128px)',
            backgroundColor: 'rgb(40, 42, 54)',
            '& > pre': { flex: '1 1 auto', mb: 'unset !important' },
          }}
        >
          <SyntaxHighlighter
            language={language}
            style={dracula}
            showLineNumbers={!wrapLine}
            wrapLines={wrapLine}
            wrapLongLines={wrapLine}
          >
            {logContent}
          </SyntaxHighlighter>

        </Box>

        <Divider />
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'center',
            p: 2
          }}
        >
          <Button
            color="primary"
            variant="contained"
            onClick={closeViewer}
          >
            Close
          </Button>
        </Box>
      </Paper>
    </Portal>
  

  )
}

export default LogViewer;
